/**
 * One heavy viewer at a time.
 *
 * Rooms and objects both open themselves as they are scrolled to, which is what makes
 * the page feel alive — and which, done naively, is six splats and five glTF objects all
 * reaching for the network at once. A browser opens about six connections to a host, so
 * everything queues behind them: the opening video, the photographs, the next room. The
 * symptom is not that any one thing is slow, it is that the whole page loads in patches
 * and nothing finishes.
 *
 * So the requests are serialised. The frame you are looking at gets the whole
 * connection, finishes in the time one file should take, and the next starts. Nothing
 * here is about how much is downloaded overall — only about refusing to ask for all of
 * it in the same instant.
 */

/** One at a time. Two halves the wait for the second frame and doubles it for the first. */
const AT_ONCE = 1;

/**
 * A viewer that never reports back must not hold the queue for ever.
 *
 * `load` on a cross-origin iframe is not guaranteed, and a splat that fails to decode
 * fires nothing at all. Without this, one stuck frame stops every room below it from
 * ever starting — which is a worse failure than loading two things at once.
 */
const PATIENCE_MS = 20000;

const waiting: (() => void)[] = [];
let running = 0;

function pump(): void {
  while (running < AT_ONCE) {
    const next = waiting.shift();
    if (next === undefined) return;
    running += 1;
    next();
  }
}

/**
 * Ask for a turn. `start` is called when one is free, and is handed the callback to
 * report completion with — which it must call exactly once, however things went.
 */
export function enqueue(start: (done: () => void) => void): void {
  waiting.push(() => {
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      running -= 1;
      pump();
    };
    window.setTimeout(done, PATIENCE_MS);
    start(done);
  });
  pump();
}

/** Test seam: forget anything still queued. */
export function resetQueue(): void {
  waiting.length = 0;
  running = 0;
}

/** What the viewer posts up to us once its subject is actually on screen. */
export const VIEWER_READY = "simam:viewer-ready";

/**
 * Resolve when the viewer in this frame says it has something to show.
 *
 * Not the iframe's own `load` event, which was the obvious choice and is the wrong one:
 * that fires when the viewer's document and modules have loaded, which is *before* it
 * has asked for the twenty-megabyte scene those modules exist to fetch. Releasing the
 * queue there let every room start its real download at once again — the queue looked
 * like it was working and was not doing anything at all.
 */
export function whenReady(iframe: HTMLIFrameElement, done: () => void): () => void {
  const onMessage = (event: MessageEvent): void => {
    // Same origin and this exact frame. A message from anywhere else is not evidence
    // about this room, whatever it claims to be.
    if (event.origin !== window.location.origin) return;
    if (event.source !== iframe.contentWindow) return;
    const data: unknown = event.data;
    if (typeof data !== "object" || data === null) return;
    if ((data as { type?: unknown }).type !== VIEWER_READY) return;
    window.removeEventListener("message", onMessage);
    done();
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}
